/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { memo, useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';

import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  FormControl,
  FormHelperText,
  Grid,
  InputBase,
  MenuItem,
  Select,
} from '@mui/material';
import { AutocompleteChangeReason, AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';

import { BootstrapInput } from '../../../../../../../../components/BootstrapInput/BootstrapInput';
import { ArrowRoundedIcon } from '../../../../../../../../components/svg-icons/ArrowRoundedIcon';
import { destructRenderInputParams } from '../../../../../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../../../../../i18n';
import { PgHba } from '../../../../../../../../types/cluster/PgHba/PgHba';
import { PgHbaConnectionTypes } from '../../../../../../../../types/cluster/PgHba/PgHbaConnectionTypes';
import { PgHbaDatabaseSpecifier } from '../../../../../../../../types/cluster/PgHba/PgHbaDatabaseSpecifier';
import { PgHbaUserSpecifier } from '../../../../../../../../types/cluster/PgHba/PgHbaUserSpecifier';
import { ACLFormValues } from '../../../../../../ClusterPage.types';
import { authMethods, connectionTypes, validationSchemaIsQuotes } from './PgHbaCard.consts';
import { usePgHbaCardStyles } from './PgHbaCard.styles';

type PgHbaCardProps = FormikProps<ACLFormValues> & {
  data: PgHba;
  databaseList: string[];
  userList: string[];
  id: number;
  index: number;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  removeItem: (id: number) => void;
  isEditable: boolean;
};

interface DragItem {
  id: number;
  index: number;
  type: string;
}

enum ItemTypes {
  CARD = 'card',
}

export const PgHbaCard = memo<PgHbaCardProps>(props => {
  const {
    id,
    index,
    moveItem,
    removeItem,
    values,
    handleChange,
    errors,
    setFieldTouched,
    setFieldError,
    setFieldValue,
    setValues,
    databaseList,
    userList,
    isEditable,
  } = props;
  const ref = useRef<HTMLDivElement>(null);
  const classes = usePgHbaCardStyles();
  const { t } = useTranslation(Namespaces.Cluster);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    item: { id, index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.CARD,
    hover: (item: DragItem, monitor) => {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  const opacity = isDragging ? 0.4 : 1;
  const cursor = isEditable ? 'move' : 'default';

  const [databaseInputValue, setDatabaseInputValue] = useState('');
  const [userInputValue, setUserInputValue] = useState('');

  const hasError = (name: keyof ACLFormValues): boolean | undefined => {
    return !!errors[name];
  };
  const getError = (name: keyof ACLFormValues): string | undefined => {
    const error = errors[name];
    if (error) {
      return t(`details.acl.validation.${name}.${error}`);
    }
    return undefined;
  };

  useEffect(() => {
    void setFieldTouched('type', true);
  }, [setFieldTouched]);

  const onInputChange = (
    _e: unknown,
    value: string,
    reason: AutocompleteInputChangeReason,
    field: 'database' | 'user',
  ) => {
    if (field === 'database') {
      setDatabaseInputValue(value);
    } else {
      setUserInputValue(value);
    }
    if (reason === 'input') {
      void validationSchemaIsQuotes.isValid(value).then(res => {
        if (!res) {
          setFieldError(field, 'not_valid');
        } else {
          setFieldError(field, '');
        }
      });
    }
  };

  const handleAutocompleteChange = (
    _e: unknown,
    _newValue: string[],
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails<string> | undefined,
    field: 'database' | 'user',
    isAllField: 'isAllDatabase' | 'isAllUser',
  ) => {
    if (reason === 'selectOption') {
      const option: string = details?.option || '';
      if (option === 'all') {
        void setValues({
          ...values,
          [isAllField]: true,
          [field]: [option],
        });
      } else if (values[field][0] === 'all' && values[isAllField]) {
        void setValues({ ...values, [isAllField]: false, [field]: [option] });
      } else {
        void setValues({
          ...values,
          [isAllField]: false,
          [field]: [...values[field], option],
        });
      }
    } else if (reason === 'createOption') {
      const option: string = details?.option || '';
      if (getError(field)) {
        if (field === 'database') {
          setDatabaseInputValue(option);
        } else {
          setUserInputValue(option);
        }
      } else if (values[field][0] === 'all' && values[isAllField]) {
        void setValues({ ...values, [isAllField]: false, [field]: [option] });
      } else if (option && values[field].indexOf(option) === -1) {
        void setValues({
          ...values,
          [isAllField]: false,
          [field]: [...values[field], option],
        });
      }
    } else if (reason === 'removeOption') {
      const option: string = details?.option || '';
      if (option === 'all' && !values[isAllField]) {
        void setValues({ ...values, [isAllField]: true, [field]: [option] });
      } else if (option) {
        void setFieldValue(
          field,
          values[field].filter(v => v !== option),
          true,
        );
      }
    } else if (reason === 'clear') {
      void setFieldValue(field, [], true);
    }
  };

  return (
    <div ref={ref} style={{ opacity, cursor }} data-cy="acl-card">
      <form className={classes.container} noValidate>
        <Grid container classes={{ root: classes.row }}>
          {isEditable ? <DragHandleIcon classes={{ root: classes.dragIcon }} /> : null}
          <Grid item xs={2} classes={{ root: classes.rowItem }}>
            <FormControl className={classes.selectFormControl} variant="filled" fullWidth error={!!errors.type}>
              <Select
                name="type"
                value={values.type}
                input={<BootstrapInput />}
                IconComponent={ArrowRoundedIcon}
                readOnly={!isEditable}
                classes={{
                  root: cx({
                    [classes.formSelect]: true,
                    [classes.formSelectError]: !!errors.type,
                  }),
                  icon: cx({
                    [classes.formIcon]: true,
                    [classes.formIconError]: !!errors.type,
                  }),
                }}
                onChange={e => handleChange(e)}
                data-cy="cluster-page-acl-tab-connection-type-select"
              >
                {connectionTypes.map((item, key: number) => (
                  <MenuItem
                    key={key}
                    value={item}
                    disabled={item === PgHbaConnectionTypes.Default}
                    data-cy={`cluster-page-acl-tab-connection-type-select-item-${item}`}
                  >
                    {item}
                  </MenuItem>
                ))}
              </Select>
              {hasError('type') && <FormHelperText className={classes.errorLine}>{getError('type')}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={3} classes={{ root: classes.rowItem }}>
            <FormControl className={classes.selectFormControl}>
              <Autocomplete
                onChange={(e, value, reason, details) => {
                  handleAutocompleteChange(e, value, reason, details, 'database', 'isAllDatabase');
                }}
                onInputChange={(e, value, reason) => {
                  onInputChange(e, value, reason, 'database');
                }}
                inputValue={databaseInputValue}
                renderInput={renderParams => (
                  <InputBase
                    name="database"
                    classes={{
                      root: cx({
                        [classes.autocompleteInputRoot]: true,
                        [classes.formSelectError]: !!errors.database,
                      }),
                      input: cx({
                        [classes.autocompleteInputMultiple]: !values.isAllDatabase,
                      }),
                    }}
                    {...destructRenderInputParams(renderParams)}
                  />
                )}
                classes={{
                  endAdornment: classes.autocompleteClear,
                  option: classes.autocompleteOption,
                }}
                ChipProps={{
                  classes: {
                    root: values.isAllDatabase ? classes.autocompleteChipRoot : classes.autocompleteChipRootMultiple,
                    label: values.isAllDatabase ? classes.autocompleteChipLabel : '',
                    deleteIcon: values.isAllDatabase ? classes.autocompleteChipIcon : '',
                  },
                }}
                options={[PgHbaDatabaseSpecifier.All, ...databaseList]}
                value={values.database}
                multiple
                freeSolo
                // TODO: Enable in https://cybertec.atlassian.net/browse/SCF-89
                // readOnly={!isEditable}
              />
            </FormControl>
            {hasError('database') && (
              <FormHelperText className={classes.errorLine}>{getError('database')}</FormHelperText>
            )}
          </Grid>
          <Grid item xs={3} classes={{ root: classes.rowItem }}>
            <FormControl className={classes.selectFormControl}>
              <Autocomplete
                onChange={(e, value, reason, details) => {
                  handleAutocompleteChange(e, value, reason, details, 'user', 'isAllUser');
                }}
                onInputChange={(e, value, reason) => {
                  onInputChange(e, value, reason, 'user');
                }}
                inputValue={userInputValue}
                renderInput={renderParams => (
                  <InputBase
                    name="user"
                    classes={{
                      root: cx({
                        [classes.autocompleteInputRoot]: true,
                        [classes.formSelectError]: !!errors.user,
                      }),
                      input: cx({
                        [classes.autocompleteInputMultiple]: !values.isAllUser,
                      }),
                    }}
                    {...destructRenderInputParams(renderParams)}
                  />
                )}
                classes={{
                  endAdornment: classes.autocompleteClear,
                  option: classes.autocompleteOption,
                }}
                ChipProps={{
                  classes: {
                    root: values.isAllUser ? classes.autocompleteChipRoot : classes.autocompleteChipRootMultiple,
                    label: values.isAllUser ? classes.autocompleteChipLabel : '',
                    deleteIcon: values.isAllUser ? classes.autocompleteChipIcon : '',
                  },
                }}
                options={[PgHbaUserSpecifier.All, ...userList]}
                value={values.user}
                multiple
                freeSolo
                // TODO: Enable in https://cybertec.atlassian.net/browse/SCF-89
                // readOnly={!isEditable}
              />
            </FormControl>
            {hasError('user') && <FormHelperText className={classes.errorLine}>{getError('user')}</FormHelperText>}
          </Grid>
          <Grid item xs={2} classes={{ root: classes.rowItem }}>
            <InputBase
              name={'address'}
              className={cx({
                [classes.formControl]: true,
                [classes.formSelectError]: hasError('address'),
              })}
              type="text"
              value={values.address}
              onChange={e => handleChange(e)}
              readOnly={!isEditable}
            />
            {hasError('address') && (
              <FormHelperText className={classes.errorLine}>{getError('address')}</FormHelperText>
            )}
          </Grid>
          <Grid item xs={2} classes={{ root: classes.rowItem }}>
            <Select
              name={'authenticationMethod'}
              value={values.authenticationMethod}
              input={<BootstrapInput />}
              IconComponent={ArrowRoundedIcon}
              classes={{ root: classes.formSelect, icon: classes.formIcon }}
              onChange={e => handleChange(e)}
              readOnly={!isEditable}
            >
              {authMethods.map((item, key: number) => (
                <MenuItem key={key} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </form>
      {isEditable ? <DeleteIcon classes={{ root: classes.deleteIcon }} onClick={() => removeItem(id)} /> : null}
    </div>
  );
});
